import { Injectable, inject } from '@angular/core';
import { FileUpload } from '@models';
import { MetricsService, SimulationService } from '@services';
import { SceneStore } from '@stores';
import { UAParser } from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '@environment';

type DebugData = {
  appVersion: string;
  model: string;
  browser: string;
  browserVersion: string;
  cores: number;
  scene: string;
  workers: {
    id: string;
    minTime: number;
    maxTime: number;
    averageTime: number;
  }[];
  metrics: {
    [key: string]: number;
  };
};

@Injectable({
  providedIn: 'root',
})
export class DebugDataService {
  private metricsService = inject(MetricsService);
  private sceneStore = inject(SceneStore);
  private simulationService = inject(SimulationService);

  constructor() {
    this.simulationService.endSimulation.subscribe(() => {
      this.finalizeTracking();
    });
  }

  debugData: DebugData = {} as DebugData;
  jsonString: string;
  debugDataUpload: FileUpload;

  workerPerformances = new Map<string, number[]>();

  updatePerformanceData(entries: { [key: string]: { cycleTime: number } }) {
    Object.entries(entries).forEach((entry) => {
      const [id, data] = entry;
      if (this.workerPerformances.has(id)) {
        this.workerPerformances.get(id).push(data.cycleTime);
      } else {
        this.workerPerformances.set(id, [data.cycleTime]);
      }
    });
  }

  finalizeTracking() {
    const userAgent = new UAParser(navigator.userAgent).getResult();
    const device = {
      model: userAgent.device.model,
      cores: navigator.hardwareConcurrency,
      browser: userAgent.browser.name,
      browserVersion: userAgent.browser.version,
    };
    this.debugData['model'] = userAgent.device.model;
    this.debugData['browser'] = device.browser;
    this.debugData['browserVersion'] = device.browserVersion;
    this.debugData['cores'] = device.cores;
    this.debugData['scene'] = this.sceneStore.path();

    this.debugData['workers'] = Array.from(
      this.workerPerformances.entries(),
    ).map(([id, data]) => {
      const minTime = Math.min(...data);
      const maxTime = Math.max(...data);
      const averageTime = Math.round(
        data.reduce((acc, val) => acc + val, 0) / data.length,
      );
      return { id, minTime, maxTime, averageTime };
    });
    this.debugData['metrics'] = this.metricsService.toolPaths();
    this.debugData['appVersion'] = environment.version;
    this.prepareDebugFile();
  }

  prepareDebugFile(): void {
    this.jsonString = JSON.stringify(this.debugData, null, 2);
    const blob = new Blob([this.jsonString], { type: 'application/json' });
    this.debugDataUpload = {
      id: uuidv4(),
      mimeType: 'application/json',
      blob,
    };
  }

  saveToFile(): void {
    const blob = new Blob([this.jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'debug.json';
    anchor.click();
    URL.revokeObjectURL(url);
  }
}
